import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, Trash2, User, ShoppingBasket, Crown, Loader2 } from 'lucide-react';
import { useKit } from '../hooks/useKit';
import { API, useAuth } from '../contexts/AuthContext';
import { essentialOils, herbs, carrierOils, findRecipe, getRecipeDetail } from '../data/mock';
import ItemPhoto from '../components/ItemPhoto';
import { toast } from 'sonner';

export default function Favorites() {
  const { items, remove } = useKit();
  const { user, openAuth } = useAuth();
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);

  const library = useMemo(() => [...essentialOils, ...carrierOils, ...herbs], []);
  const favoriteRecipes = items.filter((i) => i.type === 'recipe');

  const buildShoppingPayload = () => {
    const payload = [];
    favoriteRecipes.forEach((i) => {
      const recipe = findRecipe(i.id);
      if (!recipe) return;
      const detail = getRecipeDetail(recipe);
      detail.ingredients.forEach((ing) => payload.push({ text: ing, recipe_id: recipe.id, recipe_name: recipe.name }));
    });
    const libNames = items
      .filter((i) => i.type !== 'recipe')
      .map((i) => library.find((l) => l.id === i.id)?.name || i.name)
      .filter(Boolean);
    libNames.forEach((name) => payload.push({ text: name, recipe_id: null, recipe_name: 'Saved herbs & oils' }));
    return payload;
  };

  const sendAllToShopping = async () => {
    if (!user) { openAuth('login'); return; }
    if (!user.is_premium) {
      toast.message('Bulk shopping list is a Premium feature', {
        description: 'Upgrade to send all your favorites to one shopping list.',
        action: { label: 'Upgrade', onClick: () => navigate('/membership') },
      });
      return;
    }
    const payload = buildShoppingPayload();
    if (payload.length === 0) {
      toast.message('Nothing to add yet — favorite some recipes first.');
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/shopping-list/add-many`, { items: payload });
      const added = res.data?.added ?? 0;
      toast.success(added > 0 ? `${added} item${added > 1 ? 's' : ''} added to your shopping list.` : 'Everything is already on your shopping list.', {
        action: { label: 'View list', onClick: () => navigate('/shopping-list') },
      });
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.message('Bulk shopping list is a Premium feature', {
          description: 'Upgrade to send all your favorites to one shopping list.',
          action: { label: 'Upgrade', onClick: () => navigate('/membership') },
        });
      } else {
        toast.error('Could not build your shopping list. Please try again.');
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-10 min-h-[70vh]" data-testid="favorites-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <h1 className="font-serif text-4xl text-botanica-ink flex items-center gap-2">
          <Heart className="w-7 h-7 text-botanica-green" /> Favorites
        </h1>
        {user ? (
          <span className="flex items-center gap-1.5 text-sm text-botanica-greenDark bg-botanica-greenLight px-3 py-1.5 rounded-full">
            <User className="w-4 h-4" /> {user.name}
          </span>
        ) : (
          <button onClick={() => openAuth('login')} className="flex items-center gap-1.5 text-sm text-botanica-ink bg-botanica-card/70 hover:bg-botanica-card px-3 py-1.5 rounded-full transition-colors" data-testid="favorites-signin-btn">
            <User className="w-4 h-4" /> Sign in to sync
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-20">
          <span className="w-16 h-16 rounded-2xl bg-botanica-greenLight flex items-center justify-center mx-auto mb-4">
            <Heart className="w-7 h-7 text-botanica-green" />
          </span>
          <p className="text-botanica-muted mb-4">No favorites yet. Tap the heart on any herb, oil, or recipe to save it here.</p>
          <Link to="/library" className="inline-block text-sm px-5 py-2.5 rounded-full bg-botanica-green text-white font-medium hover:bg-botanica-greenDark transition-colors">
            Browse the library
          </Link>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-4 flex-wrap mb-6 bg-botanica-card/50 rounded-2xl px-5 py-4">
            <div>
              <p className="font-serif text-lg text-botanica-ink flex items-center gap-2">
                <Crown className="w-4 h-4 text-botanica-green" /> Shop your favorites at once
              </p>
              <p className="text-sm text-botanica-muted">Send every ingredient from your favorite recipes to one shopping list.</p>
            </div>
            <button
              onClick={sendAllToShopping}
              disabled={sending}
              className="flex items-center gap-2 bg-botanica-green text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-botanica-greenDark transition-colors disabled:opacity-70 shrink-0"
              data-testid="favorites-bulk-shopping-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : (user?.is_premium ? <ShoppingBasket className="w-4 h-4" /> : <Crown className="w-4 h-4" />)}
              {user?.is_premium ? 'Send all to shopping list' : 'Shopping list (Premium)'}
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {items.map((i) => (
              <div key={i.id} className="flex items-center gap-3 bg-botanica-card/70 rounded-2xl p-3" data-testid={`favorite-item-${i.id}`}>
                <ItemPhoto itemId={i.id} alt={i.name} className="w-16 h-16 rounded-xl shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-botanica-green font-semibold">{i.type === 'recipe' ? 'Recipe' : i.category}</p>
                  <h3 className="font-serif text-lg text-botanica-ink leading-tight truncate">{i.name}</h3>
                  {i.subtitle && <p className="text-xs text-botanica-muted italic mt-0.5 truncate">{i.subtitle}</p>}
                </div>
                <button onClick={() => remove(i.id)} className="p-2 rounded-full text-botanica-muted hover:text-red-600 hover:bg-red-50 transition-colors shrink-0" data-testid={`favorite-remove-${i.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
